import { Suspense } from 'react'
import Loading from '@/components/shared/Loading'
import type { CommonProps } from '@/@types/common'
import { useAuth } from '@/auth'
import { useThemeStore } from '@/store/themeStore'
import { useLocation } from 'react-router'
import PostLoginLayout from './PostLoginLayout'
import PreLoginLayout from './PreLoginLayout'
import { TopNotice } from '@/components/shared/LegalUpdateNotice'
import { useMobileReader } from '@/hooks/useMobileMode'
import { NotificationToaster } from '@/components/notifications/NotificationToaster'
import { AppearanceSync } from '@/components/theme/AppearanceSync'
import { TestActAsSwitcher } from '@/components/test/TestActAsSwitcher'
import { OnboardingSync } from '@/lib/onboarding'
// FeedbackWidget moved into TopBar + StudioPage mobile header as a persistent
// button (see FeedbackButton). No longer mounted globally as a floating fab.

// Routes where the BetaBanner is suppressed — the editor surfaces
// (`/d/:id` Flow editor + `/studio/:id` mobile/tablet Studio)
// have to stay focused on the document. Q22 from Claude Design 2026-05-09
// follow-up: "the editor is where focus matters most."
// Dashboard / library / settings still show the banner.
const BLANK_LAYOUT_PATHS = ['/d/', '/document/', '/studio/', '/new-document']

const Layout = ({ children }: CommonProps) => {
    const storeLayoutType = useThemeStore((state) => state.layout.type)
    const { pathname } = useLocation()

    // A phone gets the mobile reader's own screens on Home, Documents and Settings (no banner, no
    // shell bar): each has its own way back.
    const mobileReader = useMobileReader()
    const mobileHome = mobileReader && (pathname === '/' || pathname === '/documents' || pathname === '/settings' || pathname.startsWith('/settings/'))
    const isBlankRoute = mobileHome || BLANK_LAYOUT_PATHS.some((p) => pathname.startsWith(p))
    const layoutType = isBlankRoute ? 'blank' : storeLayoutType

    const { authenticated } = useAuth()

    return (
        <Suspense
            fallback={
                <div className="flex flex-auto flex-col h-[100vh]">
                    <Loading loading={true} />
                </div>
            }
        >
            {authenticated ? (
                <>
                    {/* The legal notice when the Terms changed (2c), otherwise the beta banner. */}
                    <TopNotice showLine={!isBlankRoute} />
                    {/* Notifications that arrive while you're in the app: a toast (Olivia, 10 Oct). */}
                    <NotificationToaster />
                    <AppearanceSync />
                    {/* The first steps (welcome email, first run, tour): reports/2026-10-21-welcome-onboarding.md */}
                    <OnboardingSync />
                    <TestActAsSwitcher />
                    <PostLoginLayout layoutType={layoutType}>
                        {children}
                    </PostLoginLayout>
                </>
            ) : (
                <PreLoginLayout>{children}</PreLoginLayout>
            )}
        </Suspense>
    )
}

export default Layout
